const DEFAULT_LOCAL_HINT_FONT = '700 14px ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const LOCAL_HINT_ARC_LENGTH_PX = 25 / (Math.PI / 2);
const LOCAL_HINT_TEXT_WIDTH_BUDGET_PX = 52;
const LOCAL_HINT_TEXT_HEIGHT_BUDGET_PX = 18;
const LOCAL_HINT_PIXEL_PADDING_PX = 2;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function easeOutQuad(t) {
  const x = clamp(t, 0, 1);
  return 1 - (1 - x) * (1 - x);
}

function resolveSpawnTween(player, nowMs = Date.now(), spawn = {}) {
  const now = Number.isFinite(Number(nowMs)) ? Number(nowMs) : Date.now();
  const spawnTimerMs = Math.max(0, Number(spawn.timerMs) || 0);
  const spawnAlphaMs = Math.max(0, Number(spawn.alphaMs) || 0);
  const spawnScaleDelayMs = Math.max(0, Number(spawn.scaleDelayMs) || 0);
  const spawnScaleMs = Math.max(0, Number(spawn.scaleMs) || 0);
  const spawnAtRaw = Number(player?.spawnAtMs);
  const spawnAtMs = Number.isFinite(spawnAtRaw) ? spawnAtRaw : now;
  const spawnDelayMs = Number(player?.spawnDelayMs) || 0;
  const spawnScaleStartRaw = Number(player?.spawnScaleStart);
  const spawnScaleInitial = Number.isFinite(spawnScaleStartRaw) ? spawnScaleStartRaw : 1;
  const spawnAgeMs = Math.max(0, now - spawnAtMs);
  const spawnAlphaStartMs = spawnTimerMs + spawnDelayMs;
  const spawnScaleStartMs = spawnAlphaStartMs + spawnScaleDelayMs;

  let alpha = 1;
  if (spawnAgeMs < spawnAlphaStartMs) {
    alpha = 0;
  } else if (spawnAlphaMs > 0 && spawnAgeMs < spawnAlphaStartMs + spawnAlphaMs) {
    alpha = (spawnAgeMs - spawnAlphaStartMs) / spawnAlphaMs;
  }

  let scale = 1;
  if (spawnAgeMs < spawnScaleStartMs) {
    scale = spawnScaleInitial;
  } else if (spawnScaleMs > 0 && spawnAgeMs < spawnScaleStartMs + spawnScaleMs) {
    const t = (spawnAgeMs - spawnScaleStartMs) / spawnScaleMs;
    scale = spawnScaleInitial + (1 - spawnScaleInitial) * easeOutQuad(t);
  }

  return {
    alpha,
    scale,
    spawnAtMs
  };
}

function spawnTweenDirtyExtent(baseExtent, player, nowMs = Date.now(), spawn = {}, padding = 0) {
  const extent = Math.max(0, Number(baseExtent) || 0);
  const scale = Math.max(1, Math.abs(Number(resolveSpawnTween(player, nowMs, spawn).scale) || 1));
  return extent * scale + Math.max(0, Number(padding) || 0);
}

function localTurnHintDirtyExtent(headRadius) {
  const radius = Math.max(0, Number(headRadius) || 0);
  const textCenterX = radius + 5 + LOCAL_HINT_ARC_LENGTH_PX + 10;
  const textCenterAbsY = LOCAL_HINT_ARC_LENGTH_PX + 12 + 2;
  // The hint group rotates with the player. Its farthest label corner, rather
  // than its unrotated x/y extents, determines the axis-aligned cleanup square.
  const textCornerX = textCenterX + LOCAL_HINT_TEXT_HEIGHT_BUDGET_PX / 2;
  const textCornerY = textCenterAbsY + LOCAL_HINT_TEXT_WIDTH_BUDGET_PX / 2;
  const textRadius = Math.hypot(textCornerX, textCornerY);
  const arrowRadius = Math.hypot(
    radius + 5 + LOCAL_HINT_ARC_LENGTH_PX + 3,
    LOCAL_HINT_ARC_LENGTH_PX + 3
  );
  return Math.ceil(Math.max(textRadius, arrowRadius) + LOCAL_HINT_PIXEL_PADDING_PX);
}

function drawSpawnMarker(ctx, headRadius) {
  const markerStart = headRadius + 5;
  const markerTip = markerStart + 25;
  ctx.beginPath();
  ctx.moveTo(markerStart, 0);
  ctx.lineTo(markerTip, 0);
  ctx.moveTo(markerTip, 0);
  ctx.lineTo(markerTip - 3, 3);
  ctx.moveTo(markerTip, 0);
  ctx.lineTo(markerTip - 3, -3);
  ctx.stroke();
}

function drawLocalTurnHints(ctx, headRadius, color, font) {
  const arcLen = LOCAL_HINT_ARC_LENGTH_PX;
  const offset = headRadius + 5;
  const margin = 2;
  const hintX = offset + arcLen;

  ctx.beginPath();
  ctx.moveTo(offset, 0);
  ctx.quadraticCurveTo(offset + arcLen, 0, offset + arcLen, -arcLen);
  ctx.moveTo(offset + arcLen, -arcLen);
  ctx.lineTo(offset + arcLen - 3, -arcLen + 3);
  ctx.moveTo(offset + arcLen, -arcLen);
  ctx.lineTo(offset + arcLen + 3, -arcLen + 3);
  ctx.moveTo(offset, 0);
  ctx.quadraticCurveTo(offset + arcLen, 0, offset + arcLen, arcLen);
  ctx.moveTo(offset + arcLen, arcLen);
  ctx.lineTo(offset + arcLen - 3, arcLen - 3);
  ctx.moveTo(offset + arcLen, arcLen);
  ctx.lineTo(offset + arcLen + 3, arcLen - 3);
  ctx.stroke();

  ctx.fillStyle = color;
  ctx.font = typeof font === 'string' && font ? font : DEFAULT_LOCAL_HINT_FONT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.save();
  ctx.translate(hintX + 10, -arcLen - 12 - margin);
  ctx.rotate(Math.PI / 2);
  ctx.fillText('LEFT', 0, 0);
  ctx.restore();

  ctx.save();
  ctx.translate(hintX + 10, arcLen + 10 + margin);
  ctx.rotate(Math.PI / 2);
  ctx.fillText('RIGHT', 0, 0);
  ctx.restore();
}

function drawEffectTimerRingsCore(ctx, {
  effect,
  headRadius,
  speedType,
  ringDistanceStep,
  ringDistanceOffset,
  ringLineWidth,
  ringMaxStacks,
  ringColor,
  resolveStacks,
  resolveTotalTicks
}) {
  const effectType = effect?.type;
  const ticksLeft = Number(effect?.ticks);
  if (effectType !== speedType || !Number.isFinite(ticksLeft) || ticksLeft <= 0) return;

  const rawStacks = Number(
    typeof resolveStacks === 'function'
      ? resolveStacks(effect)
      : effect?.stacks
  );
  const stacks = Number.isFinite(rawStacks) ? Math.max(1, Math.floor(rawStacks)) : 1;
  const maxStacks = Math.max(1, Math.floor(Number(ringMaxStacks) || 1));
  const displayStacks = Math.max(1, Math.min(maxStacks, stacks));

  const totalTicks = Number(
    typeof resolveTotalTicks === 'function'
      ? resolveTotalTicks(effect)
      : effect?.totalTicks
  );
  if (!Number.isFinite(totalTicks) || totalTicks <= 0) return;

  const distanceStep = Number(ringDistanceStep) || 0;
  const distanceOffset = Number(ringDistanceOffset) || 0;
  const lineWidth = Math.max(0.1, Number(ringLineWidth) || 1);
  const strokeColor = typeof ringColor === 'string' && ringColor ? ringColor : '#ffffff';

  ctx.save();
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'round';

  for (let ringIndex = 0; ringIndex < displayStacks; ringIndex += 1) {
    const ticksDone = totalTicks - ticksLeft;
    const fraction = clamp(1 - ticksDone / totalTicks, 0, 1);
    if (fraction <= 0) continue;

    const arcRadius = headRadius + distanceOffset + distanceStep * ringIndex;

    ctx.beginPath();
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = strokeColor;
    ctx.arc(0, 0, arcRadius, 0, 2 * Math.PI * fraction);
    ctx.stroke();
  }

  ctx.restore();
}

function drawHeadsCore({
  ctx,
  players,
  nowMs = Date.now(),
  fieldWidth,
  fieldHeight,
  headRadius,
  localPlayerId = -1,
  showSpawnMarkers = true,
  spawn = {},
  effect = {},
  drawHead,
  drawTrailBridge,
  localHintFont = DEFAULT_LOCAL_HINT_FONT,
  disconnectedColor = '',
  // Mesh players need a color sampled at their own position: the head is drawn
  // translated to the player, so the arena-space gradient the trail uses would
  // paint it the wrong hue. Identity for solids and for embeddings that do not
  // support mesh.
  resolvePlayerColor = null,
  clearCanvas = true
}) {
  if (!ctx) return;
  const width = Math.max(1, Number(fieldWidth) || 1);
  const height = Math.max(1, Number(fieldHeight) || 1);
  const radius = Math.max(0, Number(headRadius) || 0);
  const now = Number.isFinite(Number(nowMs)) ? Number(nowMs) : Date.now();
  const localId = Number.isFinite(Number(localPlayerId)) ? Number(localPlayerId) : -1;
  const spawnMarkerFallbackMs = Math.max(0, Number(spawn.markerFallbackMs) || 0);
  const items = Array.isArray(players) ? players : [];

  if (clearCanvas) {
    ctx.clearRect(0, 0, width, height);
  }
  for (const player of items) {
    const playerEffect = player?.effect || null;
    const isFlying = Boolean(player?.isFlying);
    const isGapping = Boolean(player?.isGapping);
    if (!player?.alive) continue;

    const playerX = Number(player?.x) || 0;
    const playerY = Number(player?.y) || 0;
    const playerAngle = Number(player?.angle) || 0;
    const flatColor = typeof player?.color === 'string' && player.color ? player.color : '#ffffff';
    const realColor = typeof resolvePlayerColor === 'function'
      ? (resolvePlayerColor(player, flatColor) || flatColor)
      : flatColor;
    // A disconnected player's head greys out to match their (server-greyed)
    // trail; it snaps back to their real color the moment they resume.
    const playerColor = (player?.connected === false && disconnectedColor)
      ? disconnectedColor
      : realColor;
    const spawnTween = resolveSpawnTween(player, now, spawn);
    const spawnAtMs = spawnTween.spawnAtMs;
    const spawnAlpha = spawnTween.alpha;
    if (spawnAlpha <= 0) continue;
    const spawnScale = spawnTween.scale;

    const markerUntilRaw = Number(player?.spawnMarkerUntilMs);
    const markerUntilMs = (Number.isFinite(markerUntilRaw) && markerUntilRaw > 0)
      ? markerUntilRaw
      : (spawnAtMs + spawnMarkerFallbackMs);
    const showMarker = Boolean(showSpawnMarkers) && now < markerUntilMs;

    const explicitLocal = player?.isLocalPlayer;
    const isLocalPlayer = explicitLocal === true
      ? true
      : explicitLocal === false
        ? false
        : Number(player?.id) === localId;

    if (!isFlying && !isGapping && typeof drawTrailBridge === 'function') {
      drawTrailBridge(ctx, player, radius, playerColor, spawnAlpha);
    }

    ctx.save();
    ctx.translate(playerX, playerY);
    ctx.rotate(playerAngle);
    ctx.scale(spawnScale, spawnScale);
    ctx.globalAlpha = spawnAlpha;
    ctx.strokeStyle = playerColor;
    ctx.lineWidth = 2;

    if (showMarker) {
      drawSpawnMarker(ctx, radius);
      if (isLocalPlayer) {
        drawLocalTurnHints(ctx, radius, playerColor, localHintFont);
      }
    }

    if (isFlying) {
      ctx.globalAlpha = spawnAlpha * 0.72;
    }

    if (typeof drawHead === 'function') {
      drawHead(ctx, player, radius, playerColor);
    } else {
      ctx.fillStyle = playerColor;
      ctx.beginPath();
      ctx.arc(0, 0, radius, 0, Math.PI * 2);
      ctx.fill();
    }

    if (isFlying) {
      ctx.globalAlpha = spawnAlpha;
      ctx.beginPath();
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.72)';
      ctx.arc(0, 0, radius + 4, 0, Math.PI * 2);
      ctx.stroke();
    }

    drawEffectTimerRingsCore(ctx, {
      effect: playerEffect,
      headRadius: radius,
      speedType: effect.speedType,
      ringSegments: effect.ringSegments,
      ringDistanceStep: effect.ringDistanceStep,
      ringDistanceOffset: effect.ringDistanceOffset,
      ringLineWidth: effect.ringLineWidth,
      ringMaxStacks: effect.ringMaxStacks,
      ringColor: effect.ringColor,
      resolveStacks: effect.resolveStacks,
      resolveTotalTicks: effect.resolveTotalTicks
    });

    ctx.restore();
  }
}

const TRAIL_BATCH_POINT_EPSILON = 0.001;

function sameTrailSegmentStyle(a, b) {
  if (!a || !b) return false;
  const ownerA = Number.isFinite(Number(a.o)) ? Number(a.o) : null;
  const ownerB = Number.isFinite(Number(b.o)) ? Number(b.o) : null;
  return a.c === b.c
    && Number(a.r) === Number(b.r)
    && ownerA === ownerB;
}

function trailSegmentTouches(a, b) {
  if (!a || !b) return false;
  return Math.abs((Number(a.x2) || 0) - (Number(b.x1) || 0)) <= TRAIL_BATCH_POINT_EPSILON
    && Math.abs((Number(a.y2) || 0) - (Number(b.y1) || 0)) <= TRAIL_BATCH_POINT_EPSILON;
}

function canBatchTrailSegment(batch, op) {
  if (!Array.isArray(batch) || batch.length === 0) return true;
  const prev = batch[batch.length - 1];
  return sameTrailSegmentStyle(prev, op) && trailSegmentTouches(prev, op);
}

// Mesh colors paint as a gradient fixed to the arena rather than a flat color.
// The trail ops still carry the player's flat hex, so the swap happens here:
// the app registers a resolver that maps a color string to a CanvasGradient
// built for that context. Left null in every other embedding (the replay
// tools), where trails stay flat.
let trailPaintResolver = null;

function setTrailPaintResolver(resolver) {
  trailPaintResolver = typeof resolver === 'function' ? resolver : null;
}

function resolveTrailPaint(ctx, color) {
  if (!trailPaintResolver) return color;
  return trailPaintResolver(ctx, color) || color;
}

function drawTrailSegmentBatchDefault(ctx, batch) {
  if (!Array.isArray(batch) || batch.length === 0) return;
  const first = batch[0];
  const r = Number(first?.r) || 0;
  if (r <= 0) return;

  ctx.strokeStyle = resolveTrailPaint(ctx, first?.c || '#ffffff');
  ctx.lineWidth = r * 2;
  ctx.beginPath();
  ctx.moveTo(Number(first?.x1) || 0, Number(first?.y1) || 0);
  for (const op of batch) {
    ctx.lineTo(Number(op?.x2) || 0, Number(op?.y2) || 0);
  }
  ctx.stroke();
}

function drawTrailCapDefault(ctx, op) {
  const r = Number(op?.r) || 0;
  if (r <= 0) return;
  const x = Number(op?.x) || 0;
  const y = Number(op?.y) || 0;
  ctx.fillStyle = resolveTrailPaint(ctx, op?.c || '#ffffff');
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

function applyTrailOpsCore(
  ctx,
  ops,
  {
    onClear = null,
    onErase = null,
    onSegment = null,
    onCap = null,
    lineCap = 'butt',
    lineJoin = 'round'
  } = {}
) {
  if (!ctx) return;
  if (!Array.isArray(ops) || ops.length === 0) return;

  ctx.save();
  ctx.lineCap = lineCap;
  ctx.lineJoin = lineJoin;
  const segmentBatch = [];
  const shouldBatchSegments = typeof onSegment !== 'function';
  const flushSegmentBatch = () => {
    if (segmentBatch.length === 0) return;
    drawTrailSegmentBatchDefault(ctx, segmentBatch);
    segmentBatch.length = 0;
  };

  for (const op of ops) {
    if (!op || typeof op !== 'object') continue;
    if (op.k === 'c') {
      flushSegmentBatch();
      if (typeof onClear === 'function') onClear(op, ctx);
      continue;
    }
    if (op.k === 'r') {
      flushSegmentBatch();
      if (typeof onErase === 'function') onErase(op, ctx);
      continue;
    }
    if (op.k === 's') {
      if (typeof onSegment === 'function') {
        flushSegmentBatch();
        onSegment(op, ctx);
      } else {
        const r = Number(op?.r) || 0;
        if (r <= 0) continue;
        if (!shouldBatchSegments || !canBatchTrailSegment(segmentBatch, op)) {
          flushSegmentBatch();
        }
        segmentBatch.push(op);
      }
      continue;
    }
    if (op.k === 'p') {
      flushSegmentBatch();
      if (typeof onCap === 'function') {
        onCap(op, ctx);
      } else {
        drawTrailCapDefault(ctx, op);
      }
    }
  }

  flushSegmentBatch();
  ctx.restore();
}

export {
  applyTrailOpsCore,
  drawEffectTimerRingsCore,
  drawHeadsCore,
  localTurnHintDirtyExtent,
  resolveSpawnTween,
  setTrailPaintResolver,
  spawnTweenDirtyExtent
};
